import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  createSetting,
  deleteSetting,
  getSetting,
  listSettings,
  updateSetting,
} from "./settingsService";

const empty = {
  first_name: "",
  surname: "",
  position: "SALES AGENT",
  branch: "",
  email: "",
  phone: "",
  address: "",
  ap_supplier_code: "",
  is_active: true,
};

function IconButton({
  icon,
  title,
  className = "",
  ...props
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      className={`settings-icon-action ${className}`}
      {...props}
    >
      <i className={`bi bi-${icon}`}></i>
    </button>
  );
}

export default function SalesPersons() {
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(empty);
  const [editorOpen, setEditorOpen] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    const [salesPersons, branchList] = await Promise.all([
      listSettings("sales-persons"),
      listSettings("branches"),
    ]);

    setItems(salesPersons);
    setBranches(branchList);
  };

  useEffect(() => {
    load().catch(() =>
      toast.error("Could not load sales persons.")
    );
  }, []);

  const filteredItems = useMemo(() => {
    const term = search.trim().toUpperCase();

    if (!term) return items;

    return items.filter((item) =>
      `${item.code} ${item.first_name} ${item.surname} ${item.branch_name}`
        .toUpperCase()
        .includes(term)
    );
  }, [items, search]);

  const open = async (item) => {
    try {
      const full = await getSetting(
        "sales-persons",
        item.id
      );
      setSelected(full);
      setForm(full);
    } catch {
      toast.error("Could not open this sales person.");
    }
  };

  const startNew = () => {
    setSelected(null);
    setForm(empty);
    setEditorOpen(true);
  };

  const save = async (event) => {
    event.preventDefault();

    try {
      const saved = selected
        ? await updateSetting(
            "sales-persons",
            selected.id,
            form
          )
        : await createSetting("sales-persons", form);

      await load();
      await open(saved);
      setEditorOpen(false);
      toast.success("Sales person saved.");
    } catch (error) {
      toast.error(
        Object.values(error.response?.data || {})
          .flat()
          .join(" ") || "Could not save sales person."
      );
    }
  };

  const remove = async (item) => {
    if (!window.confirm("Delete this sales person?")) {
      return;
    }

    try {
      await deleteSetting("sales-persons", item.id);

      if (selected?.id === item.id) {
        setSelected(null);
        setForm(empty);
      }

      await load();
    } catch {
      toast.error(
        "This sales person cannot be deleted while in use."
      );
    }
  };

  return (
    <section className="settings-panel modern-setup-panel">
      <div className="settings-panel__head">
        <div>
          <h2>Sales persons</h2>
          <p>
            Manage agents, branch assignments and commission
            activity.
          </p>
        </div>

        <button
          className="settings-primary-action"
          onClick={startNew}
        >
          <i className="bi bi-person-plus"></i>
          New sales person
        </button>
      </div>

      <div className="setup-toolbar">
        <div className="setup-search">
          <i className="bi bi-search"></i>
          <input
            value={search}
            placeholder="Search by agent number, name or branch"
            onChange={(event) =>
              setSearch(event.target.value)
            }
          />
        </div>

        <span className="setup-record-count">
          {filteredItems.length} sales person
          {filteredItems.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="settings-table-wrap setup-main-table">
        <table>
          <thead>
            <tr>
              <th>Agent number</th>
              <th>Sales person</th>
              <th>Position</th>
              <th>Branch</th>
              <th>Commission balance</th>
              <th className="table-actions-column">
                Actions
              </th>
            </tr>
          </thead>

          <tbody>
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan="6" className="settings-empty">
                  No sales persons found.
                </td>
              </tr>
            ) : (
              filteredItems.map((item) => (
                <tr
                  key={item.id}
                  className={
                    selected?.id === item.id
                      ? "selected-row"
                      : ""
                  }
                  onClick={() => open(item)}
                >
                  <td>
                    <span className="record-code">
                      {item.code}
                    </span>
                  </td>
                  <td>
                    <div className="person-cell">
                      <span>
                        {item.first_name?.[0]}
                        {item.surname?.[0]}
                      </span>
                      <div>
                        <strong>
                          {item.first_name} {item.surname}
                        </strong>
                        <small>
                          {item.email || item.phone || "—"}
                        </small>
                      </div>
                    </div>
                  </td>
                  <td>{item.position}</td>
                  <td>{item.branch_name || "—"}</td>
                  <td>{item.commission_balance || "0.00"}</td>
                  <td
                    className="table-actions-column"
                    onClick={(event) =>
                      event.stopPropagation()
                    }
                  >
                    <IconButton
                      icon="eye"
                      title="Open"
                      onClick={() => open(item)}
                    />
                    <IconButton
                      icon="pencil-square"
                      title="Edit"
                      onClick={async () => {
                        await open(item);
                        setEditorOpen(true);
                      }}
                    />
                    <IconButton
                      icon="trash3"
                      title="Delete"
                      className="danger"
                      onClick={() => remove(item)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!selected ? (
        <div className="setup-empty-state">
          <div>
            <i className="bi bi-person-badge"></i>
          </div>
          <h3>Select a sales person</h3>
          <p>
            Open a record to review contact details,
            commissions and linked payments.
          </p>
        </div>
      ) : (
        <div className="record-detail-workspace">
          <div className="record-detail-header">
            <div className="person-detail-heading">
              <div className="person-detail-avatar">
                {selected.first_name?.[0]}
                {selected.surname?.[0]}
              </div>

              <div>
                <span className="record-code">
                  {selected.code}
                </span>
                <h3>
                  {selected.first_name} {selected.surname}
                </h3>
                <p>
                  {selected.position} ·{" "}
                  {selected.branch_name || "NO BRANCH"}
                </p>
              </div>
            </div>

            <button
              type="button"
              className="secondary-action"
              onClick={() => setEditorOpen(true)}
            >
              <i className="bi bi-pencil-square"></i>
              Edit profile
            </button>
          </div>

          <div className="person-information-grid">
            <InfoItem
              label="Email address"
              value={selected.email || "—"}
              icon="envelope"
            />
            <InfoItem
              label="Phone number"
              value={selected.phone || "—"}
              icon="telephone"
            />
            <InfoItem
              label="Address"
              value={selected.address || "—"}
              icon="geo-alt"
            />
            <InfoItem
              label="AP supplier link"
              value={
                selected.ap_supplier_code || "NOT LINKED"
              }
              icon="link-45deg"
            />
          </div>

          <div className="commission-summary modern">
            <div>
              <i className="bi bi-graph-up"></i>
              <span>Total commission</span>
              <strong>
                {selected.total_commission || "0.00"}
              </strong>
            </div>

            <div>
              <i className="bi bi-check2-circle"></i>
              <span>Paid commission</span>
              <strong>
                {selected.paid_commission || "0.00"}
              </strong>
            </div>

            <div>
              <i className="bi bi-wallet2"></i>
              <span>Commission balance</span>
              <strong>
                {selected.commission_balance || "0.00"}
              </strong>
            </div>
          </div>

          <section className="linked-history-section">
            <div className="child-workspace__heading">
              <h4>Payment and commission history</h4>
              <p>
                Commission entries linked to this sales
                person.
              </p>
            </div>

            <div className="settings-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Payment code</th>
                    <th>Payment date</th>
                    <th>Percentage</th>
                    <th>Commission</th>
                  </tr>
                </thead>

                <tbody>
                  {selected.commission_entries?.length ? (
                    selected.commission_entries.map(
                      (entry) => (
                        <tr key={entry.id}>
                          <td>
                            <span className="record-code">
                              {entry.payment_code}
                            </span>
                          </td>
                          <td>
                            {entry.payment_date || "—"}
                          </td>
                          <td>{entry.percentage}</td>
                          <td>
                            {entry.commission_amount}
                          </td>
                        </tr>
                      )
                    )
                  ) : (
                    <tr>
                      <td
                        colSpan="4"
                        className="settings-empty"
                      >
                        No linked commission payments yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {editorOpen && (
        <SalesPersonModal
          selected={selected}
          form={form}
          setForm={setForm}
          branches={branches}
          onClose={() => setEditorOpen(false)}
          onSave={save}
        />
      )}
    </section>
  );
}

function InfoItem({ label, value, icon }) {
  return (
    <div className="info-item">
      <i className={`bi bi-${icon}`}></i>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
    </div>
  );
}

function SalesPersonModal({
  selected,
  form,
  setForm,
  branches,
  onClose,
  onSave,
}) {
  return (
    <div
      className="setup-modal-backdrop"
      onMouseDown={onClose}
    >
      <div
        className="setup-modal"
        onMouseDown={(event) =>
          event.stopPropagation()
        }
      >
        <div className="setup-modal__header">
          <div>
            <span>
              {selected?.code || "NEW SALES PERSON"}
            </span>
            <h3>
              {selected
                ? "Edit sales person"
                : "Create sales person"}
            </h3>
          </div>

          <button
            type="button"
            className="modal-close"
            onClick={onClose}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <form
          className="modern-editor-form"
          onSubmit={onSave}
        >
          <label>
            <span>First name</span>
            <input
              value={form.first_name || ""}
              onChange={(event) =>
                setForm({
                  ...form,
                  first_name:
                    event.target.value.toUpperCase(),
                })
              }
              required
            />
          </label>

          <label>
            <span>Surname</span>
            <input
              value={form.surname || ""}
              onChange={(event) =>
                setForm({
                  ...form,
                  surname:
                    event.target.value.toUpperCase(),
                })
              }
              required
            />
          </label>

          <label>
            <span>Position</span>
            <select
              value={form.position || "SALES AGENT"}
              onChange={(event) =>
                setForm({
                  ...form,
                  position: event.target.value,
                })
              }
            >
              <option>SALES AGENT</option>
              <option>BROKER</option>
              <option>MANAGER</option>
              <option>OTHER</option>
            </select>
          </label>

          <label>
            <span>Branch</span>
            <select
              value={form.branch || ""}
              onChange={(event) =>
                setForm({
                  ...form,
                  branch: event.target.value,
                })
              }
              required
            >
              <option value="">SELECT...</option>
              {branches.map((branch) => (
                <option
                  key={branch.id}
                  value={branch.id}
                >
                  {branch.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Email address</span>
            <input
              type="email"
              value={form.email || ""}
              onChange={(event) =>
                setForm({
                  ...form,
                  email:
                    event.target.value.toLowerCase(),
                })
              }
            />
          </label>

          <label>
            <span>Phone number</span>
            <input
              value={form.phone || ""}
              onChange={(event) =>
                setForm({
                  ...form,
                  phone: event.target.value,
                })
              }
            />
          </label>

          <label className="editor-wide">
            <span>Address</span>
            <input
              value={form.address || ""}
              onChange={(event) =>
                setForm({
                  ...form,
                  address:
                    event.target.value.toUpperCase(),
                })
              }
            />
          </label>

          <label className="editor-wide">
            <span>AP supplier link</span>
            <input
              value={form.ap_supplier_code || ""}
              placeholder="Optional supplier code"
              onChange={(event) =>
                setForm({
                  ...form,
                  ap_supplier_code:
                    event.target.value.toUpperCase(),
                })
              }
            />
          </label>

          <div className="setup-modal__actions">
            <button
              type="button"
              className="secondary-action"
              onClick={onClose}
            >
              Cancel
            </button>

            <button type="submit">
              <i className="bi bi-check2-circle"></i>
              {selected ? "Update profile" : "Save profile"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
